import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_API_BASE_URL;

export default function AcceptInvite() {
  const { t } = useTranslation();
  const ROLE_LABELS = { ORG_ADMIN: t('AcceptInvite.roleOrgAdmin'), MEMBER: t('AcceptInvite.roleMember'), AUDITOR: t('AcceptInvite.roleAuditor'), VIEWER: t('AcceptInvite.roleViewer') };
  const token = new URLSearchParams(window.location.hash.split('?')[1] || '').get('token');
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ userName: '', password: '', confirm: '', phoneNumber: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setError(t('AcceptInvite.errorNoToken')); return; }
    axios.get(`${API}/org-team/invite/${token}`)
      .then(r => {
        setInvite(r.data);
        const suggested = ((r.data.firstName || '') + (r.data.lastName || '')).toLowerCase().replace(/\s+/g, '.');
        setForm(f => ({ ...f, userName: suggested }));
      })
      .catch(() => setError(t('AcceptInvite.errorInvalidLink')));
  }, [token, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) { setError(t('AcceptInvite.errorPasswordMismatch')); return; }
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/org-team/accept/${token}`, {
        userName: form.userName, password: form.password, phoneNumber: form.phoneNumber,
      });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data || t('AcceptInvite.errorRegistrationFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const card = {
    minHeight: '100vh', background: '#f1f5f9', display: 'flex',
    alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, system-ui, sans-serif',
  };
  const box = {
    background: '#fff', borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    padding: '2.5rem', width: '100%', maxWidth: 420,
  };

  if (error && !invite) return (
    <div style={card}><div style={box}>
      <h2 style={{ color: '#1e293b', marginBottom: 8 }}>{t('AcceptInvite.invalidInvitationHeading')}</h2>
      <p style={{ color: '#dc2626' }}>{error}</p>
      <a href="#/login" style={{ color: '#1e40af' }}>{t('AcceptInvite.goToLoginLink')}</a>
    </div></div>
  );

  if (!invite) return <div style={card}><div style={box}><p>{t('AcceptInvite.loading')}</p></div></div>;

  if (done) return (
    <div style={card}><div style={box}>
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <i className="fas fa-check-circle" style={{ fontSize: 48, color: '#10b981' }} />
      </div>
      <h2 style={{ color: '#1e293b', textAlign: 'center' }}>{t('AcceptInvite.welcomeHeading')}</h2>
      <p style={{ color: '#64748b', textAlign: 'center' }}>{t('AcceptInvite.accountCreatedPrefix')}<strong>{invite.organizationName}</strong>{t('AcceptInvite.accountCreatedSuffix')}</p>
      <a href="#/login" style={{
        display: 'block', textAlign: 'center', marginTop: 16, padding: '0.75rem',
        background: '#1e3a5f', color: '#fff', borderRadius: 8, textDecoration: 'none', fontWeight: 600,
      }}>{t('AcceptInvite.goToLoginButton')}</a>
    </div></div>
  );

  return (
    <div style={card}>
      <div style={box}>
        <h2 style={{ color: '#1e293b', marginBottom: 4 }}>{t('AcceptInvite.invitedHeading')}</h2>
        <p style={{ color: '#64748b', marginBottom: 20 }}>
          {t('AcceptInvite.joinPrefix')}<strong>{invite.organizationName}</strong>{t('AcceptInvite.joinConnector')}<strong>{ROLE_LABELS[invite.orgRole] || invite.orgRole}</strong>
        </p>
        {invite.message && (
          <div style={{
            background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
            padding: '10px 14px', marginBottom: 20, fontSize: 13, color: '#475569',
          }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{t('AcceptInvite.personalMessageLabel')}</div>
            {invite.message}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          {[[t('AcceptInvite.labelFirstName'), 'text', 'firstName', invite.firstName || '', true],
            [t('AcceptInvite.labelLastName'), 'text', 'lastName', invite.lastName || '', true],
            [t('AcceptInvite.labelEmail'), 'email', 'email', invite.email, true]].map(([label, type, key, val, readOnly]) => (
            <div key={key} style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: '#374151', marginBottom: 4 }}>{label}</label>
              <input type={type} value={val} readOnly={readOnly} style={{
                width: '100%', padding: '0.625rem 0.75rem', border: '1px solid #e2e8f0',
                borderRadius: 7, fontSize: '0.9rem', background: readOnly ? '#f8fafc' : '#fff',
                boxSizing: 'border-box',
              }} />
            </div>
          ))}
          {[[t('AcceptInvite.labelUsername'), 'text', 'userName'], [t('AcceptInvite.labelPhoneNumber'), 'tel', 'phoneNumber'],
            [t('AcceptInvite.labelPassword'), 'password', 'password'], [t('AcceptInvite.labelConfirmPassword'), 'password', 'confirm']].map(([label, type, key]) => (
            <div key={key} style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: '#374151', marginBottom: 4 }}>{label}</label>
              <input type={type} value={form[key]} required={label.includes('*')}
                onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                style={{
                  width: '100%', padding: '0.625rem 0.75rem', border: '1px solid #e2e8f0',
                  borderRadius: 7, fontSize: '0.9rem', boxSizing: 'border-box',
                }} />
            </div>
          ))}
          {error && <div style={{ color: '#dc2626', fontSize: '0.8125rem', marginBottom: 12 }}>{error}</div>}
          <button type="submit" disabled={submitting} style={{
            width: '100%', padding: '0.75rem', background: '#1e3a5f', color: '#fff',
            border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '1rem', cursor: 'pointer',
          }}>
            {submitting ? t('AcceptInvite.buttonCreating') : t('AcceptInvite.buttonCreate')}
          </button>
        </form>
      </div>
    </div>
  );
}
